/**
 * Every role exposed by the role picker, listed in descending privilege.
 * Used by gating logic (middleware, sidebar visibility, terminal nav) and
 * the picker UI in `FormSharedPrimaryPermissions.vue`.
 */
export const ROLES = [
  'super_admin',
  'admin',
  'supervisor',
  'reservations',
  'front_desk',
  'front_desk_link_pay',
  'sell_and_redeem',
  'sell_and_redeem_without_discounts',
  'redeem',
] as const;

export type Role = (typeof ROLES)[number];

/**
 * Canonical permission set for every role.
 *
 * Exact array equality against this map is how the picker decides which
 * radio option to highlight for a given user. Keep entries sorted so the
 * sets remain comparable.
 */
export const ROLE_PERMISSIONS: Record<Role, string[]> = {
  super_admin: [
    'manage_account',
    'manage_subscription',
    'manage_users',
    'manage_catalogue',
    'manage_fulfilment',
    'manage_payments',
    'manage_domains',
    'view_analytics',
    'view_orders',
    'view_transactions',
    'view_logs',
    'process_card_payments',
    'process_offline_payments',
    'process_test_orders',
    'apply_discounts',
    'redeem_vouchers',
    'extend_vouchers',
    'refund_vouchers',
  ],
  admin: [
    'manage_catalogue',
    'manage_fulfilment',
    'view_analytics',
    'view_logs',
    'view_transactions',
    'view_orders',
    'process_card_payments',
    'process_offline_payments',
    'process_test_orders',
    'apply_discounts',
    'redeem_vouchers',
    'extend_vouchers',
    'refund_vouchers',
  ],
  supervisor: [
    'view_orders',
    'view_transactions',
    'process_card_payments',
    'process_offline_payments',
    'process_test_orders',
    'apply_discounts',
    'redeem_vouchers',
    'extend_vouchers',
    'refund_vouchers',
  ],
  sell_and_redeem: [
    'process_card_payments',
    'process_offline_payments',
    'apply_discounts',
    'redeem_vouchers',
  ],
  reservations: ['view_orders', 'process_card_payments', 'redeem_vouchers'],
  front_desk: [
    'manage_fulfilment',
    'view_orders',
    'process_offline_payments',
    'redeem_vouchers',
  ],
  front_desk_link_pay: [
    'manage_fulfilment',
    'view_orders',
    'process_card_payments',
    'redeem_vouchers',
  ],
  sell_and_redeem_without_discounts: [
    'process_card_payments',
    'process_offline_payments',
    'redeem_vouchers',
  ],
  redeem: ['redeem_vouchers'],
};

const SUPER_ADMIN_MARKERS = [
  'manage_account',
  'manage_subscription',
  'manage_users',
  'manage_payments',
  'manage_domains',
];

const ADMIN_MARKERS = ['manage_catalogue', 'view_analytics', 'view_logs'];

/**
 * Classify a user by their permissions. Returns one of the nine roles
 * (used by gating logic and by the role picker). Throws on permission
 * sets that don't map to any role — i.e. empty or otherwise exotic
 * combinations. The auth store's `role` getter swallows the throw and
 * surfaces `''` so the UI treats those users as roleless.
 *
 * @param permissions
 */
export const roleFromPermissions = (permissions: string[]): Role => {
  const has = (permission: string): boolean => permissions.includes(permission);

  if (SUPER_ADMIN_MARKERS.some(has)) {
    return 'super_admin';
  }

  if (ADMIN_MARKERS.some(has)) {
    return 'admin';
  }

  // Precedence: supervisor, front desk, reservations. Front desk sets hold
  // view_orders but never apply_discounts, so they fall past the supervisor check.
  if (has('view_orders') && has('apply_discounts')) {
    return 'supervisor';
  }

  if (has('manage_fulfilment')) {
    if (has('process_offline_payments')) {
      return 'front_desk';
    }

    if (has('process_card_payments')) {
      return 'front_desk_link_pay';
    }
  }

  if (has('view_orders')) {
    return 'reservations';
  }

  // Either payment-processing permission marks a seller.
  const isSeller =
    has('process_card_payments') || has('process_offline_payments');

  if (has('apply_discounts') && isSeller) {
    return 'sell_and_redeem';
  }

  if (isSeller) {
    return 'sell_and_redeem_without_discounts';
  }

  if (has('redeem_vouchers')) {
    return 'redeem';
  }

  throw new Error(
    `roleFromPermissions: no role for permissions [${permissions.join(', ')}]`,
  );
};

/**
 * Lookup table from sorted permission-set signature to the role whose
 * canonical set produced it. Built once at module load so the picker's
 * `exactRoleFromPermissions` is O(n log n) on the input rather than
 * sorting every canonical set on every call.
 */
const CANONICAL_SIGNATURE_TO_ROLE = new Map<string, Role>(
  ROLES.map((role): [string, Role] => [
    [...ROLE_PERMISSIONS[role]].sort().join(','),
    role,
  ]),
);

/**
 * Strict classification used by the role picker. Returns the exact role whose
 * canonical permission set matches the supplied permissions, or `''` if the
 * user has a non-canonical permission combination.
 *
 * @param permissions
 */
export const exactRoleFromPermissions = (permissions: string[]): Role | '' => {
  const signature = [...permissions].sort().join(',');

  return CANONICAL_SIGNATURE_TO_ROLE.get(signature) ?? '';
};

export const ROLE_LABELS: Record<Role, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  supervisor: 'Supervisor',
  sell_and_redeem: 'Sell and redeem with staff discounts',
  reservations: 'Reservations',
  front_desk: 'Front desk (offline only)',
  front_desk_link_pay: 'Front desk (link pay)',
  sell_and_redeem_without_discounts: 'Sell and redeem',
  redeem: 'Redeem',
};
